import * as React from "react";
import { Block, TExtendedBlockProps } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { Currency } from "kanopi-components/Currency";
import { frequencyToString } from "kw/util/utils";
import { HighlightRectangle } from "kanopi-components/HighlightRectangle/HighlightRectangle";
import { isNullish } from "kanopi-components/utils/primitives";
import { SIZE, Spinner } from "baseui/spinner";
import { Tag } from "kanopi-components/Tag";
import { TagProps } from "baseui/tag";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import {
  DisplaySmall,
  LabelSmall,
  ParagraphLarge,
  HeadingSmall,
  LabelXSmall,
  LabelMedium,
  ParagraphMedium,
} from "kanopi-components/typography";
import { QuoteMessage } from "./QuoteMessage";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { ContactUsModal } from "kw/components/ContactUs/ContactUs";
import { Image } from "kanopi-components/Image";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import { useFormatter } from "kw/util/useFormatter";

/* eslint-disable react/prop-types */

// TODO: Enable the proptype check next time when this file is changed

export function QuotePrice(props) {
  const {
    periodicPrice,
    paymentFrequency,
    pricingFailed,
    priceRiskProfile,
    loading,
    inline = false,
    highlight = true,
    productKey,
    draftUpdate,
    pricingOthers,
  } = props;

  let content: JSX.Element;

  const draftUpdateMessages = draftUpdate?.quotes?.[0].messages;
  const isDraftUpdateReferred = draftUpdate?.riskProfile.state === "REFERRED";
  const isPriceZero = periodicPrice === 0;

  const { formatCurrency, formatPercent } = useFormatter();

  const formatterMap = {
    percentage: formatPercent,
    currency: formatCurrency,
  };

  if (loading) {
    content = <PriceLoading />;
  } else if (isDraftUpdateReferred && isPriceZero) {
    content = <Referred quoteId={draftUpdate?.quotes?.[0]?.id} />;
  } else if (draftUpdateMessages) {
    content = <QuoteMessage messages={JSON.parse(draftUpdateMessages)} />;
  } else if (pricingFailed) {
    content = <RepriceRiskProfile onClick={priceRiskProfile} inline={inline} />;
  } else if (isNullish(periodicPrice)) {
    content = <PriceUnavailable />;
  } else if (inline) {
    content = (
      <PriceAvailableInline
        periodicPrice={periodicPrice}
        paymentFrequency={paymentFrequency}
        inline={inline}
        productKey={productKey}
      />
    );
  } else {
    const parsedPricingOthers = pricingOthers && JSON.parse(pricingOthers);
    content = (
      <>
        <PriceAvailable
          periodicPrice={periodicPrice}
          paymentFrequency={paymentFrequency}
          inline={inline}
          productKey={productKey}
        />
        {parsedPricingOthers?.display?.length > 0 && (
          <Block marginTop="scale200">
            {parsedPricingOthers?.display?.map((item) => (
              <LabelSmall key={item.label} data-testid={`${productKey}-${item.label}`}>
                {item.label}: {formatterMap[item.type](item.value)}
              </LabelSmall>
            ))}
          </Block>
        )}
        <LabelSmall></LabelSmall>
      </>
    );
  }

  return highlight ? (
    <HighlightRectangle {...(inline && { minHeight: 0 })}>{content}</HighlightRectangle>
  ) : (
    content
  );
}

export function PriceLoading() {
  return (
    <>
      <Block marginBottom="scale200">
        <Spinner $size={SIZE.small} />
      </Block>
      <LabelSmall>Loading...</LabelSmall>
    </>
  );
}

function RepriceRiskProfile(props) {
  const { onClick, inline } = props;
  return (
    <>
      <Block
        marginBottom="scale200"
        display={inline ? "flex" : null}
        justifyContent={inline ? ["center", "center", "flex-start"] : null}
      >
        <Button size={Button.SIZE.compact} fluid onClick={onClick} NORMAL_WIDTH_RESET>
          Retry
        </Button>
      </Block>
      <LabelSmall>Something went wrong</LabelSmall>
    </>
  );
}

export const PriceUnavailable = () => {
  return <ParagraphLarge>Price not available</ParagraphLarge>;
};

type TPriceAvailable = {
  periodicPrice: number;
  paymentFrequency: string;
  productKey?: string;
};
export const PriceAvailable: React.FC<TPriceAvailable> = (props) => {
  const { periodicPrice, paymentFrequency } = props;
  return (
    <Block>
      <DisplaySmall data-testid="periodicPrice">
        <Currency value={periodicPrice} />
      </DisplaySmall>
      <LabelSmall data-testid="paymentFrequency">
        {frequencyToString(paymentFrequency)}
      </LabelSmall>
    </Block>
  );
};

export const PriceAvailableInline: React.FC<TPriceAvailable & TExtendedBlockProps> = (
  props,
) => {
  const { periodicPrice, paymentFrequency, productKey, ...blockProps } = props;
  const [, theme] = useStyletron();
  return (
    <Block
      display="flex"
      alignItems="center"
      gridColumnGap={theme.sizing.scale400}
      {...blockProps}
    >
      <LabelXSmall>Gross Premium <br/> (incl. GST)</LabelXSmall>
      <HeadingSmall
        margin={0}
        data-testid={`${productKey}-periodicPrice`}
        color="inherit"
      >
        <Currency value={periodicPrice} />
      </HeadingSmall>
      <LabelXSmall
        margin={0}
        data-testid={`${productKey}-paymentFrequency`}
        color="inherit"
      >
        {frequencyToString(paymentFrequency)}
      </LabelXSmall>
    </Block>
  );
};

export const PriceAvailableWithLabel: React.FC<TPriceAvailable & TagProps> = (
  props,
) => {
  const { periodicPrice, paymentFrequency, productKey, ...tagProps } = props;
  const [, theme] = useStyletron();
  return (
    <Tag
      closeable={false}
      overrides={{
        Root: {
          style: {
            height: "auto",
            paddingTop: theme.sizing.scale100,
            paddingBottom: theme.sizing.scale100,
          },
        },
        StartEnhancerContainer: {
          style: {
            flex: "1 1 0",
          },
        },
      }}
      {...tagProps}
    >
      <PriceAvailableInline
        periodicPrice={periodicPrice}
        paymentFrequency={paymentFrequency}
        productKey={productKey}
      />
    </Tag>
  );
};

const Referred = ({ quoteId }: { quoteId: number }) => {
  return (
    <SidePadder data-testid="referred">
      <Block display="flex" alignItems="center" justifyContent="center">
        <VerticalSpacer maxWidth={["100%", "100%", "100%", "50%"]} alignItems="center">
          <Image
            src={"icon-agent.svg"}
            alt="Agent icon"
            maxWidth="100px"
            strictRatio
            style={{
              marginTop: "32px",
              marginBottom: "32px",
            }}
          />
          <LabelMedium center>We&apos;re sorry for the inconvenience.</LabelMedium>

          <ParagraphMedium center margin="0">
            Based on your previous answers, we are unable to provide you with a quote.
            Please <ContactUsModal buttonText="contact" /> one of our agents if you wish
            to discuss this quote
          </ParagraphMedium>

          <ParagraphMedium center margin="0">
            Your quote reference number:{" "}
            <Block>
              <b>#{quoteId}</b>
            </Block>
          </ParagraphMedium>
        </VerticalSpacer>
      </Block>
    </SidePadder>
  );
};
